// THE FRAME A SPLAT'S SPHERICAL HARMONICS LIVE IN, as one rule.
//
// Spherical harmonics are DIRECTIONAL: a set of coefficients is only valid in
// the frame it was fitted in. A loader that bakes a pose into positions and
// covariances — which `loadEnvironmentSplats` must, because the avatar and the
// room share one mesh and a mesh carries one matrix — moves the geometry and
// leaves the coefficients behind, so the view-dependent term is evaluated
// against the wrong axes.
//
// The symptom is specific and was chased for a long time before it was named:
// the DC colour stays correct (band 0 is a constant and rotation-invariant),
// ODD bands change sign under a 180 degree pose while even ones do not, and
// edges pick up a colour fringe. Measured on Startup_Level, whose pose IS a 180
// degree flip, it cost 3.44 against 0.89 mean |RGB| from the Spark reference.
//
// The fix is to take the DIRECTION into the coefficients' frame rather than to
// rotate the coefficients. The two are equivalent — evaluating a rotated basis
// against d equals evaluating the original basis against R^T d — but the
// coefficients are stored as quantised bytes, and rotating a band redistributes
// its norm across components, so re-encoding can clip the [-1, 1] the encoding
// allows. Rotating the direction is exact and costs one 3x3 per splat. (The SPZ
// reference rotates the coefficients instead, at PACK time, before quantising,
// where that objection does not apply.)

import { Matrix3 } from 'three'

/**
 * The rotation taking a WORLD direction into the frame a splat's spherical
 * harmonics were fitted in, given the pose a loader baked into its geometry.
 *
 * The bake is a rotation plus a UNIFORM scale plus a translation. Translation
 * cannot reach a direction and uniform scale drops out of a normalised one, so
 * what is left is the rotation, and the inverse of an orthonormal matrix is its
 * transpose. The scale is divided out explicitly rather than relied on to
 * normalise away: that only holds while it IS uniform, which is this function's
 * to check rather than to assume.
 *
 * @param {import('three').Matrix4} bakeMatrix - the placement baked into the
 *   positions and covariances. Identity for a loader that bakes nothing.
 * @param {Matrix3} [target] - written in place when given.
 * @return {Matrix3} world -> coefficient frame.
 */
export function shRotationForBake( bakeMatrix, target = new Matrix3() ) {

	target.setFromMatrix4( bakeMatrix );

	const e = target.elements;
	// Column lengths are the per-axis scales. A non-uniform one cannot be divided
	// out by a single factor, and a direction under it is not rotated but sheared,
	// so there is no 3x3 that makes the harmonics right — say so rather than
	// return a plausible matrix.
	const sx = Math.hypot( e[ 0 ], e[ 1 ], e[ 2 ] );
	const sy = Math.hypot( e[ 3 ], e[ 4 ], e[ 5 ] );
	const sz = Math.hypot( e[ 6 ], e[ 7 ], e[ 8 ] );

	if ( sx === 0 || sy === 0 || sz === 0 ) {

		throw new Error( 'shRotationForBake: the bake matrix is degenerate' );

	}

	const spread = Math.max( sx, sy, sz ) / Math.min( sx, sy, sz );

	if ( spread > 1.001 ) {

		throw new Error(
			`shRotationForBake: the bake scale is not uniform (${ sx.toFixed( 4 ) }, `
			+ `${ sy.toFixed( 4 ) }, ${ sz.toFixed( 4 ) }); a sheared direction has no `
			+ 'spherical-harmonic rotation'
		);

	}

	target.multiplyScalar( 1 / sx );

	return target.transpose();

}

/** The uniform scale a bake applied, for reporting. */
export function bakeScale( bakeMatrix ) {

	const e = bakeMatrix.elements;
	return Math.hypot( e[ 0 ], e[ 1 ], e[ 2 ] );

}
